import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { EndCustomerService } from 'src/app/services/end-customer.service';
import { MasterDataService } from 'src/app/services/master-data.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import Swal from 'sweetalert2';
import { CustomChip, Position, Role } from 'src/app/models/staff-model';
import { ChipType } from 'src/app/enums/staff-management-enum';

@Component({
  selector: 'app-staff-filter-popup',
  templateUrl: './staff-filter-popup.component.html',
  styleUrls: ['./staff-filter-popup.component.scss']
})
export class StaffFilterPopupComponent implements OnInit {
  filterForm!: FormGroup;
  statusTypes: string[] = ['Active', 'Inactive'];
  roles: Role[] = [];
  positions: Position[] = [];
  chips: CustomChip[] = [];
  oldChips: CustomChip[] = [];

  roleControl = new FormControl('');
  positionControl = new FormControl('');
  statusControl = new FormControl('');

  selectedRoles: string[] = [];
  selectedPositions: string[] = [];
  selectedStatus: string = '';

  filteredRoles: Role[] = [];
  filteredPositions: Position[] = [];
  filteredStatusTypes: string[] = [];

  @Output() clearChipsAndFilters = new EventEmitter<any>();
  
  @ViewChild('roleAutocompleteTrigger') roleAutocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('positionAutocompleteTrigger') positionAutocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('statusAutocompleteTrigger') statusAutocompleteTrigger!: MatAutocompleteTrigger;

  constructor(
    private dialogRef: MatDialogRef<StaffFilterPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { filterData: any, chips: any },
    private fb: FormBuilder,
    private endCustomerService: EndCustomerService,
    private masterService: MasterDataService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.filterForm = this.fb.group({
      role: [[]],
      positionName: [[]],
      status: ['']
    });

    this.chips = this.data.chips || [];
    this.oldChips = this.data.chips || [];

    if (this.data.filterData) {
      this.filterForm.patchValue(this.data.filterData);
      
      this.selectedRoles = this.data.filterData.role || [];
      this.selectedPositions = this.data.filterData.positionName || [];
      this.selectedStatus = this.data.filterData.status || '';
    }

    this.roleControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterRoles(value || ''))
    ).subscribe(filtered => {
      this.filteredRoles = filtered;
    });

    this.positionControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterPositions(value || ''))
    ).subscribe(filtered => {
      this.filteredPositions = filtered;
    });

    this.statusControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterStatusTypes(value || ''))
    ).subscribe(filtered => {
      this.filteredStatusTypes = filtered;
    });

    this.loadMasterData();
  }

  private _filterRoles(value: string): Role[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    return this.roles.filter(role => 
      role.roleName.toLowerCase().includes(filterValue)
    );
  }

  private _filterPositions(value: string): Position[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    return this.positions.filter(position => 
      position.position.toLowerCase().includes(filterValue) 
    );
  }

  private _filterStatusTypes(value: string): string[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    return this.statusTypes.filter(status => 
      status.toLowerCase().includes(filterValue)
    );
  }

  private loadMasterData(): void {
    const role = this.localStorageService.getItem('roleName');
    const url = `/role-type/all/roleTypes`;
    const queryparams = {
      role: role,
      pageSize: 50,
      pageNumber: 0,
      keyWord: '',
      roleMappingId: role === 'OEM' ? this.localStorageService.getItem('oemId') : role === 'DISTRIBUTOR' ? this.localStorageService.getItem('distributorId') : ''
    };
  
    this.endCustomerService.executeGetRequestURL(url, queryparams)
      .pipe(finalize(() => { }))
      .subscribe(response => {
        this.roles = response.data || [];
        this.roles = this.roles.sort((a: any, b: any) => {
          const nameA = a?.roleName?.toLowerCase() || '';
          const nameB = b?.roleName?.toLowerCase() || '';
          return nameA.localeCompare(nameB);
        });
        this.filteredRoles = this.roles;
      });
  
    this.masterService.getPositions(0, 50, '')
      .pipe(finalize(() => { }))
      .subscribe(response => {
        this.positions = response.data || [];
        this.filteredPositions = this.positions;
      });
  }
  

  onRoleChange(event: any) {
    const role = event.option.value;
    if (!this.selectedRoles.includes(role.roleName)) {
      this.selectedRoles.push(role.roleName);
      
      this.filterForm.patchValue({
        role: this.selectedRoles
      });
      
      this.addNewChip({
        id: 0,
        name: role.roleName,
        type: ChipType.ROLE,
        priority: 1
      });
    }
    this.roleControl.setValue('');
    event.option.deselect();
  }

  onPositionChange(event: any) {
    const position = event.option.value;
    if (!this.selectedPositions.includes(position.position)) {
      this.selectedPositions.push(position.position);
      
      this.filterForm.patchValue({
        positionName: this.selectedPositions
      });
      
      this.addNewChip({
        id: 0,
        name: position.position,
        type: ChipType.POSITION,
        priority: 2
      });
    }
    this.positionControl.setValue('');
    event.option.deselect();
  }

  onStatusChange(event: any) {
    const status = event.option.value;
    this.selectedStatus = status;
    
    this.filterForm.patchValue({
      status: status
    });
    
    this.chips = this.chips.filter(chip => chip.type !== ChipType.STATUS);
    this.addNewChip({
      id: 0,
      name: status,
      type: ChipType.STATUS,
      priority: 3
    });
    
    this.statusControl.setValue('');
    event.option.deselect();
  }

  addNewChip(newChip: CustomChip): void {
    const chipExists = this.chips.some(chip =>
      chip.name === newChip.name && chip.type === newChip.type
    );

    if (!chipExists) {
      this.chips = [...this.chips, newChip];
    }
  }

  remove(chip: CustomChip): void {
    const index = this.chips.indexOf(chip);
    if (index >= 0) {
      this.chips.splice(index, 1);
    }
  
    switch (chip.type) {
      case ChipType.ROLE:
        this.selectedRoles = this.selectedRoles.filter(r => r !== chip.name);
        this.filterForm.patchValue({ role: this.selectedRoles });
        break;
      case ChipType.POSITION:
        this.selectedPositions = this.selectedPositions.filter(p => p !== chip.name);
        this.filterForm.patchValue({ positionName: this.selectedPositions });
        break;
      case ChipType.STATUS:
        this.selectedStatus = '';
        this.filterForm.patchValue({ status: '' });
        break;
    }
  }

  submitFilter(): void {
    const filterData = {
      role: this.selectedRoles.length ? this.selectedRoles : '',
      positionName: this.selectedPositions.length ? this.selectedPositions : '',
      status: this.selectedStatus || ''
    };

    if (
      (!Array.isArray(filterData.role) || !filterData.role.length) && 
      (!Array.isArray(filterData.positionName) || !filterData.positionName.length) && 
      !filterData.status
    ) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Filters',
        text: 'Please select at least one filter!',
        confirmButtonText: 'OK',
      });
      return;
    }

    const payload = {
      filterData: filterData,
      chips: this.chips
    };

    this.dialogRef.close(payload);
  }

  clearFilter(): void {
    this.chips = [];
    this.selectedRoles = [];
    this.selectedPositions = [];
    this.selectedStatus = '';
    this.filterForm.reset();
    this.roleControl.setValue('');
    this.positionControl.setValue('');
    this.statusControl.setValue('');

    const filters = {
      role: '',
      positionName: '',
      status: ''
    };

    this.clearChipsAndFilters.emit({
      clearingChips: this.chips,
      clearingFilters: filters
    });
  }

  cancel(): void {
    this.chips = [];
    this.dialogRef.close({ filterData: null, chips: [] });
  }

  displayRole(role: Role): string {
    return role ? role.roleName : '';
  }

  displayPosition(position: Position): string {
    return position ? position.position : '';
  }
}